/**
 * Pre-flight checklist for CreateExperiment Review step before launch.
 */

import { useMemo } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Alert01Icon,
  CheckmarkCircle01Icon,
  Loading01Icon,
} from "@hugeicons/core-free-icons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

type PreflightCheck = {
  id: string
  label: string
  status: "pass" | "warn" | "fail" | "pending"
  detail?: string
}

type QAPreviewProps = {
  controlUrl: string
  variantUrl: string
  /** Same-URL element test with instruction count on B. */
  isElementTest?: boolean
  changesCount?: number
  goalSet?: boolean
  trafficValid?: boolean
  sampleTargetRealistic?: boolean
  /** "About 6 weeks to a result." from the Setup plan */
  sampleDetail?: string
  className?: string
}

function buildChecks(props: QAPreviewProps): PreflightCheck[] {
  const {
    controlUrl,
    variantUrl,
    isElementTest,
    changesCount = 0,
    goalSet = true,
    trafficValid = true,
    sampleTargetRealistic = true,
    sampleDetail,
  } = props

  const urlsPresent = Boolean(controlUrl?.trim()) && Boolean(variantUrl?.trim())
  const urlsDiffer = isElementTest ? true : controlUrl.trim() !== variantUrl.trim()
  const variantsDiffer = isElementTest ? changesCount > 0 : urlsDiffer

  return [
    {
      id: "goal",
      label: "Measurement goal set",
      status: goalSet ? "pass" : "fail",
      detail: goalSet ? undefined : "Pick a goal on the Setup step.",
    },
    {
      id: "urls",
      label: isElementTest ? "Page URL set" : "Variant URLs set",
      status: urlsPresent ? "pass" : "fail",
      detail: urlsPresent ? undefined : "Enter a page URL for every variant.",
    },
    {
      id: "differ",
      label: isElementTest ? "Variant has edits" : "Variant URLs are unique",
      // An element test with no edits splits traffic between two identical
      // pages: it can only end in "no difference", so it blocks launch.
      status: variantsDiffer ? "pass" : isElementTest ? "fail" : "warn",
      detail: variantsDiffer
        ? isElementTest
          ? `${changesCount} ${changesCount === 1 ? "change" : "changes"} on Variant B`
          : undefined
        : isElementTest
          ? "Open the editor and save at least one change. Otherwise B matches control."
          : "Each variant needs a distinct live URL.",
    },
    {
      id: "traffic",
      label: "Traffic split valid",
      status: trafficValid ? "pass" : "fail",
      detail: trafficValid ? "Shares sum to 100%." : "Traffic shares must sum to 100%.",
    },
    {
      id: "sample",
      label: "Sample target realistic",
      status: sampleTargetRealistic ? "pass" : "warn",
      detail: sampleTargetRealistic
        ? sampleDetail ?? "Duration estimate looks feasible."
        : `${sampleDetail ?? "This test may take months."} Pick a busier page or a goal more visitors reach.`,
    },
  ]
}

export function QAPreview({
  controlUrl,
  variantUrl,
  isElementTest = false,
  changesCount = 0,
  goalSet = true,
  trafficValid = true,
  sampleTargetRealistic = true,
  sampleDetail,
  className,
}: QAPreviewProps) {
  const checks = useMemo(
    () =>
      buildChecks({
        controlUrl,
        variantUrl,
        isElementTest,
        changesCount,
        goalSet,
        trafficValid,
        sampleTargetRealistic,
        sampleDetail,
      }),
    [
      controlUrl,
      variantUrl,
      isElementTest,
      changesCount,
      goalSet,
      trafficValid,
      sampleTargetRealistic,
      sampleDetail,
    ],
  )

  const passCount = checks.filter((c) => c.status === "pass").length
  const failCount = checks.filter((c) => c.status === "fail").length
  const warnCount = checks.filter((c) => c.status === "warn").length
  const ready = failCount === 0 && passCount >= 4

  return (
    <Card className={cn("border-border/30 gap-0", className)}>
      <CardHeader className="border-b border-border/40 pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-sm font-medium text-foreground">Before you launch</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {passCount} of {checks.length} checks passed
              {warnCount > 0 ? ` · ${warnCount} warning${warnCount === 1 ? "" : "s"}` : ""}
              {failCount > 0 ? ` · ${failCount} blocking` : ""}
            </p>
          </div>
          <Badge
            variant="outline"
            className={cn(
              "text-xs font-medium leading-4 px-1.5 py-0.5 rounded-md",
              ready
                ? "bg-success/10 text-success dark:text-success border-success/30"
                : failCount > 0
                  ? "bg-destructive/10 text-destructive border-destructive/30"
                  : "bg-warning/10 text-warning dark:text-warning border-warning/30",
            )}
          >
            {ready ? "Ready to launch" : failCount > 0 ? "Blocked" : "Review warnings"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Pre-flight checklist */}
        <ul className="grid sm:grid-cols-2 gap-2">
          {checks.map((check) => (
            <li
              key={check.id}
              className="flex items-start gap-2 rounded-lg border border-border/30 bg-muted/10 px-3 py-2"
            >
              <StatusIcon status={check.status} />
              <div className="min-w-0">
                <p className="text-[12px] font-medium text-foreground leading-snug">
                  {check.label}
                </p>
                {check.detail && (
                  <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                    {check.detail}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function StatusIcon({ status }: { status: PreflightCheck["status"] }) {
  if (status === "pass") {
    return (
      <HugeiconsIcon
        icon={CheckmarkCircle01Icon}
        size={14}
        className="mt-0.5 shrink-0 text-success"
      />
    )
  }
  if (status === "fail") {
    return (
      <HugeiconsIcon
        icon={Alert01Icon}
        size={14}
        className="mt-0.5 shrink-0 text-destructive"
      />
    )
  }
  if (status === "warn") {
    return (
      <HugeiconsIcon
        icon={Alert01Icon}
        size={14}
        className="mt-0.5 shrink-0 text-warning"
      />
    )
  }
  return (
    <HugeiconsIcon
      icon={Loading01Icon}
      size={14}
      className="mt-0.5 shrink-0 text-muted-foreground animate-spin"
    />
  )
}

/** True when preflight has no blocking failures. */
export function isQAReady(props: QAPreviewProps): boolean {
  return buildChecks(props).every((c) => c.status !== "fail")
}
